/**
 * Portal frontend API client.
 *
 * Sprint 01 (RLY-006): wires up the env-driven base URL and a typed JSON
 * helper. Real auth headers, error envelope handling, and SSE wiring land in
 * later stories (RLY-014, RLY-022, RLY-025). Frontend code must funnel all
 * HTTP through `apiFetch` so we have a single chokepoint for tenant headers
 * and error handling.
 *
 * Sprint 02 (RLY-015 AC #6 wire-up): imports `MeResponseSchema` from
 * `@simpliwork/api-schema` and exposes a `fetchMe` helper that runtime-parses
 * `GET /api/v1/me` against the shared schema.
 *
 * Sprint 03 (RLY-013): `fetchMe` accepts an optional `getToken` callback so
 * the caller can plumb Clerk's `useAuth().getToken()` through. When provided
 * AND it returns a non-null token, an `Authorization: Bearer <token>` header
 * is attached. Existing callers (RLY-015 tests) that pass only `baseUrl`
 * remain valid — the new arg is optional. The helper now also parses non-2xx
 * bodies as the canonical `ErrorEnvelopeSchema` from `@simpliwork/api-schema`
 * (RLY-029) and exposes them as a typed `ApiError`.
 *
 * Sprint 04 (RLY-220): the previous `?? "http://localhost:8787"` unconditional
 * fallback violated standing-order [04] — production bundles silently pointed
 * at localhost when `VITE_API_BASE_URL` was unset. The fallback is now gated
 * behind `import.meta.env.DEV`; non-dev builds throw at module load with a
 * clear error. Resolution is extracted into the pure `resolveApiBaseUrl`
 * function so the throw path is unit-testable without monkey-patching
 * `import.meta.env` (mirrors the `loadClerkPublishableKey` pattern from
 * RLY-013).
 *
 * Sprint 06 (RLY-024): extends the client with `fetchAgents`, `fetchAgent`,
 * `createAgent`, `updateAgent`, `fetchAgentVersions`, `fetchRuns`, `fetchRun`,
 * and `triggerRun` — all funneled through the same `apiFetch` chokepoint so
 * the auth/error/envelope handling stays consistent.
 *
 * Cross-package wires (per [12]):
 *   - Schemas: `AgentResponseSchema`, `AgentDetailResponseSchema`,
 *     `AgentVersionResponseSchema`, `CreateAgentRequestSchema`,
 *     `CreateAgentResponseSchema`, `UpdateAgentRequestSchema`,
 *     `UpdateAgentResponseSchema`, `RunResponseSchema`, `ListRunsResponseSchema`,
 *     `TriggerRunRequestSchema`, `TriggerRunResponseSchema` from
 *     `@simpliwork/api-schema` (`packages/api-schema/src/agents.ts`,
 *     `packages/api-schema/src/runs.ts`).
 *   - API endpoints: `apps/portal-api/src/routes/agents.ts:196` (POST),
 *     `:237` (GET list), `:286` (GET detail), `:403` (PATCH),
 *     `:442` (GET versions); `apps/portal-api/src/routes/runs.ts:364`
 *     (POST trigger), `:403` (GET list), `:436` (GET detail).
 */
import {
  type AgentDetailResponse,
  AgentDetailResponseSchema,
  type AgentResponse,
  AgentResponseSchema,
  type AgentToolPackagesResponse,
  AgentToolPackagesResponseSchema,
  type TriggerAssignment,
  TriggerAssignmentSchema,
  type TriggerListResponse,
  TriggerListResponseSchema,
  type UpsertTriggerRequest,
  type AgentVersionResponse,
  AgentVersionResponseSchema,
  type BookingLinkResponse,
  BookingLinkResponseSchema,
  // Contact update (L4)
  type ContactResponse,
  ContactResponseSchema,
  type UpdateContactRequest,
  UpdateContactRequestSchema,
  // Content module + analytics
  type BrandVoiceListResponse,
  BrandVoiceListResponseSchema,
  type BrandVoiceResponse,
  BrandVoiceResponseSchema,
  type ContentAnalyticsItem,
  type ContentAnalyticsResponse,
  ContentAnalyticsResponseSchema,
  type ContentPostListResponse,
  ContentPostListResponseSchema,
  type ContentPostResponse,
  ContentPostResponseSchema,
  type CreateAgentRequest,
  type CreateAgentResponse,
  CreateAgentResponseSchema,
  type CreateBrandVoiceRequest,
  type CreateContentPostRequest,
  // Leads module
  type CreateLeadRequest,
  type CreateLeadResponse,
  CreateLeadResponseSchema,
  type CreateSecretRequest,
  type EmailConnectionConnectResponse,
  EmailConnectionConnectResponseSchema,
  type EmailConnectionResponse,
  EmailConnectionResponseSchema,
  ErrorEnvelopeSchema,
  type ErrorPayload,
  type FunnelResponse,
  FunnelResponseSchema,
  type GeneratePostRequest,
  type InboxLeadListResponse,
  InboxLeadListResponseSchema,
  type InboxLeadResponse,
  type IntegrationConnectResponse,
  IntegrationConnectResponseSchema,
  type IntegrationsListResponse,
  IntegrationsListResponseSchema,
  type LeadAnalyticsResponse,
  LeadAnalyticsResponseSchema,
  type LeadDetailResponse,
  LeadDetailResponseSchema,
  type LeadListResponse,
  LeadListResponseSchema,
  type LeadMessageListResponse,
  LeadMessageListResponseSchema,
  type LeadMessageResponse,
  LeadMessageResponseSchema,
  type LeadResponse,
  LeadResponseSchema,
  type LeadSequenceListResponse,
  LeadSequenceListResponseSchema,
  type LeadSequenceResponse,
  LeadSequenceResponseSchema,
  type ListRunsResponse,
  ListRunsResponseSchema,
  MeResponseSchema,
  type PlatformConnectionListResponse,
  PlatformConnectionListResponseSchema,
  type PlatformConnectionResponse,
  PlatformConnectionResponseSchema,
  paginatedResponse,
  type RunResponse,
  RunResponseSchema,
  type SchedulePostRequest,
  type SecretCreateResponse,
  SecretCreateResponseSchema,
  type SecretListResponse,
  SecretListResponseSchema,
  type SequenceStepListResponse,
  SequenceStepListResponseSchema,
  type SequenceStepResponse,
  type SocialPlatform,
  type StartSequenceRequest,
  type TriggerRunRequest,
  type TriggerRunResponse,
  TriggerRunResponseSchema,
  type TwilioSettingsResponse,
  TwilioSettingsResponseSchema,
  type UpdateAgentRequest,
  type UpdateAgentResponse,
  UpdateAgentResponseSchema,
  type UpdateAgentToolPackagesRequest,
  type UpdateBookingLinkRequest,
  UpdateBookingLinkRequestSchema,
  type UpdateBrandVoiceRequest,
  type UpdateContentPostRequest,
  type UpdateEmailConnectionRequest,
  type UpdateLeadRequest,
  type UpdateLeadStatusRequest,
  type UpdateSequenceStatusRequest,
  type UpdateTwilioSettingsRequest,
  UpdateTwilioSettingsRequestSchema,
  type UpsertPlatformConnectionRequest,
} from "@simpliwork/api-schema";
import type { z } from "zod";

/**
 * Error thrown at module load when `VITE_API_BASE_URL` is unset in a non-dev
 * build. Standing order [04] — fail loudly at boot rather than silently
 * pointing production traffic at a localhost dev port.
 */
export class MissingApiBaseUrlError extends Error {
  constructor() {
    super(
      "VITE_API_BASE_URL is not set. Production builds must define it (e.g. " +
        "Cloudflare Pages → Settings → Environment Variables → " +
        "`VITE_API_BASE_URL=https://api.simpliwork.bot`). For local dev, copy " +
        "`apps/portal-frontend/.env.example` to `.env.local`. Standing order " +
        "[04]: no silent localhost fallback past DEV.",
    );
    this.name = "MissingApiBaseUrlError";
  }
}

/**
 * Resolve the API base URL from a Vite-style env shape.
 *
 * Pure function (no `import.meta.env` reference inside) so tests can exercise
 * every branch by passing literal env objects — no `vi.stubEnv` gymnastics.
 *
 *   - `VITE_API_BASE_URL` set (any mode) → returned verbatim.
 *   - Unset + `DEV === true` → returns the localhost dev fallback.
 *   - Unset + `DEV !== true` → throws `MissingApiBaseUrlError`.
 */
export function resolveApiBaseUrl(env: { VITE_API_BASE_URL?: string; DEV?: boolean }): string {
  const raw = env.VITE_API_BASE_URL?.trim();
  if (raw) return raw;

  if (env.DEV === true) {
    // Local dev convenience — wrangler dev defaults to :8787. Vite's dead-code
    // elimination on `import.meta.env.DEV` ensures this branch never reaches
    // production bundles.
    return "http://localhost:8787";
  }

  throw new MissingApiBaseUrlError();
}

const API_BASE_URL: string = resolveApiBaseUrl({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  DEV: import.meta.env.DEV,
});

/** Inferred type for the `GET /api/v1/me` response. Source of truth: `@simpliwork/api-schema`. */
export type Me = z.infer<typeof MeResponseSchema>;

/**
 * Optional async callback that returns a session token (typically
 * `useAuth().getToken()` from Clerk). Returning `null` or `undefined` skips
 * the `Authorization` header — that is the SignedOut path.
 */
export type GetTokenFn = () => Promise<string | null | undefined>;

/**
 * Typed error thrown by `apiFetch` / `fetchMe` for any non-2xx response.
 *
 * `payload` is the parsed `ErrorEnvelopeSchema` body when the response body
 * conformed to the canonical envelope, otherwise a synthetic
 * `{ code: "unknown", message: response.statusText }`. Per RLY-029 AC #4 the
 * frontend never trusts the wire format blindly — parse-or-fallback.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: ErrorPayload,
    public readonly rawBody: unknown,
  ) {
    super(payload.message ?? `API request failed: ${status}`);
    this.name = "ApiError";
  }
}

async function buildErrorPayload(
  response: Response,
): Promise<{ payload: ErrorPayload; raw: unknown }> {
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    raw = await response.text().catch(() => null);
  }
  const parsed = ErrorEnvelopeSchema.safeParse(raw);
  if (parsed.success) {
    return { payload: parsed.data.error, raw };
  }
  // Fallback per RLY-029 AC #4 — keep the frontend resilient to envelope drift.
  return {
    payload: {
      code: "internal_error",
      message: response.statusText || `HTTP ${response.status}`,
    },
    raw,
  };
}

export interface ApiFetchOptions extends RequestInit {
  /** Called once per request to mint an `Authorization` header. */
  getToken?: GetTokenFn;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  // Don't auto-set Content-Type for FormData — the browser must set it with
  // the multipart boundary itself. Auto-set application/json for everything
  // else when the caller didn't override.
  if (
    options.body !== undefined &&
    !headers.has("Content-Type") &&
    !(typeof FormData !== "undefined" && options.body instanceof FormData)
  ) {
    headers.set("Content-Type", "application/json");
  }

  if (options.getToken && !headers.has("Authorization")) {
    const token = await options.getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const { getToken: _ignored, ...init } = options;
  void _ignored;
  const response = await fetch(url, { ...init, headers });

  if (!response.ok) {
    const { payload, raw } = await buildErrorPayload(response);
    throw new ApiError(response.status, payload, raw);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetch and runtime-validate `GET /api/v1/me`.
 *
 * Uses `MeResponseSchema.parse` so a contract drift between API and frontend
 * surfaces as a `ZodError` at the boundary. `baseUrl` is passed in (rather than
 * read from `API_BASE_URL`) so callers can inject a test base URL without
 * monkey-patching `import.meta.env`.
 *
 * `getToken` is optional. Frontend callers that mount under `<SignedIn>` will
 * pass Clerk's `useAuth().getToken()`; tests and Storybook stubs can omit it.
 * If provided AND it returns a token, the request carries `Authorization:
 * Bearer <token>`; otherwise the request is unauthenticated and the API will
 * respond 401.
 */
export async function fetchMe(baseUrl: string, getToken?: GetTokenFn): Promise<Me> {
  const headers: Record<string, string> = {};
  if (getToken) {
    const token = await getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${baseUrl}/api/v1/me`, { headers });
  if (!res.ok) {
    const { payload, raw } = await buildErrorPayload(res);
    throw new ApiError(res.status, payload, raw);
  }
  const json = await res.json();
  return MeResponseSchema.parse(json);
}

// ─── RLY-024 — Agents + Runs API helpers ─────────────────────────────────────
//
// All helpers funnel through `apiFetch`. They:
//   1. Take the `getToken` callback so the caller can plumb Clerk's
//      `useAuth().getToken()` in. Mandatory in production; optional only so
//      tests can omit it.
//   2. Runtime-parse responses through the canonical Zod schemas in
//      `@simpliwork/api-schema`. Drift between API and frontend surfaces as a
//      `ZodError` at the boundary.
//   3. Re-throw on non-2xx via the typed `ApiError` already built by `apiFetch`.

/** Paginated `GET /api/v1/agents` response schema. Inferred type used as wire shape. */
const AgentListResponseSchema = paginatedResponse(AgentResponseSchema);
export type AgentListResponse = z.infer<typeof AgentListResponseSchema>;

/** Paginated `GET /api/v1/agents/:id/versions` response schema. */
const AgentVersionListResponseSchema = paginatedResponse(AgentVersionResponseSchema);
export type AgentVersionListResponse = z.infer<typeof AgentVersionListResponseSchema>;

export interface ListRunsParams {
  agentId?: string;
  status?: RunResponse["status"];
  limit?: number;
  cursor?: string;
}

/** GET /api/v1/agents — paginated list of agents in the active tenant. */
export async function fetchAgents(
  options: { getToken?: GetTokenFn; limit?: number; cursor?: string } = {},
): Promise<AgentListResponse> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.cursor !== undefined) params.set("cursor", options.cursor);
  const query = params.toString();
  const path = `/api/v1/agents${query ? `?${query}` : ""}`;
  const json = await apiFetch<unknown>(path, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AgentListResponseSchema.parse(json);
}

/** GET /api/v1/agents/:id — full agent detail including current_version_config. */
export async function fetchAgent(
  agentId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<AgentDetailResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AgentDetailResponseSchema.parse(json);
}

/** GET /api/v1/agents/:id/versions — version history (newest first). */
export async function fetchAgentVersions(
  agentId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<AgentVersionListResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}/versions`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AgentVersionListResponseSchema.parse(json);
}

/** POST /api/v1/agents — create an agent (mints initial v1). */
export async function createAgent(
  body: CreateAgentRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<CreateAgentResponse> {
  const json = await apiFetch<unknown>("/api/v1/agents", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return CreateAgentResponseSchema.parse(json);
}

/** PATCH /api/v1/agents/:id — bumps the agent version. */
export async function updateAgent(
  agentId: string,
  body: UpdateAgentRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<UpdateAgentResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return UpdateAgentResponseSchema.parse(json);
}

/** GET /api/v1/runs — paginated list of runs (filterable by agent_id / status). */
export async function fetchRuns(
  options: { getToken?: GetTokenFn } & ListRunsParams = {},
): Promise<ListRunsResponse> {
  const params = new URLSearchParams();
  if (options.agentId) params.set("agent_id", options.agentId);
  if (options.status) params.set("status", options.status);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  const path = `/api/v1/runs${query ? `?${query}` : ""}`;
  const json = await apiFetch<unknown>(path, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ListRunsResponseSchema.parse(json);
}

/** GET /api/v1/runs/:id — full run detail. */
export async function fetchRun(
  runId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<RunResponse> {
  const json = await apiFetch<unknown>(`/api/v1/runs/${runId}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return RunResponseSchema.parse(json);
}

/** GET /api/v1/runs/:id/cost-events — paid-call cost events for a run. */
export async function fetchRunCostEvents(
  runId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").RunCostEventsResponse> {
  const { RunCostEventsResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>(`/api/v1/runs/${runId}/cost-events`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return RunCostEventsResponseSchema.parse(json);
}

/**
 * POST /api/v1/agents/:id/runs — trigger a run for an agent.
 *
 * Returns the queued run id + status (always `"pending"` on first delivery,
 * may also be `"pending"` on idempotent replay per RLY-022 wire shape).
 */
export async function triggerRun(
  agentId: string,
  body: TriggerRunRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<TriggerRunResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}/runs`, {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TriggerRunResponseSchema.parse(json);
}

// ─── RLY-026 — Secrets API helpers ─────────────────────────────────────────
//
// Backed by RLY-023 producer endpoints (Sprint 06):
//   POST   /api/v1/secrets        → CreateSecretRequest → SecretCreateResponse (201)
//   GET    /api/v1/secrets        → SecretListResponse  (NAMES + metadata; never plaintext)
//   DELETE /api/v1/secrets/:name  → 204
//
// Cross-package wires (per [12]):
//   - Schemas: `CreateSecretRequestSchema`, `SecretCreateResponseSchema`,
//     `SecretListResponseSchema` from `@simpliwork/api-schema`
//     (`packages/api-schema/src/secrets.ts:66`, `:83`, `:56`).
//   - API endpoints: `apps/portal-api/src/routes/secrets.ts:191` (POST),
//     `:220` (GET), `:266` (DELETE).
//
// **Hard rule per RLY-023**: read responses NEVER include the secret value.
// The `SecretListResponseSchema` deliberately has no `value` field; a
// schema-side runtime check fails closed if the wire ever drifts.

/** GET /api/v1/secrets — paginated list of secret names + metadata. */
export async function fetchSecrets(
  options: { getToken?: GetTokenFn } = {},
): Promise<SecretListResponse> {
  const json = await apiFetch<unknown>("/api/v1/secrets", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return SecretListResponseSchema.parse(json);
}

/** POST /api/v1/secrets — create or rotate. Response NEVER echoes the value. */
export async function createSecret(
  body: CreateSecretRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<SecretCreateResponse> {
  const json = await apiFetch<unknown>("/api/v1/secrets", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return SecretCreateResponseSchema.parse(json);
}

/** DELETE /api/v1/secrets/:name — no body; 204 on success. */
export async function deleteSecret(
  name: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/secrets/${encodeURIComponent(name)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// ── Leads module API functions ───────────────────────────────────────────────

export async function fetchLeadsAnalytics(
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadAnalyticsResponse> {
  const json = await apiFetch<unknown>("/api/v1/leads/analytics", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadAnalyticsResponseSchema.parse(json);
}

export async function fetchLeads(
  params: { status?: LeadResponse["status"]; limit?: number; cursor?: string } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const json = await apiFetch<unknown>(`/api/v1/leads${query}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadListResponseSchema.parse(json);
}

export async function fetchLead(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadDetailResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(id)}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadDetailResponseSchema.parse(json);
}

export async function createLead(
  body: CreateLeadRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<CreateLeadResponse> {
  const json = await apiFetch<unknown>("/api/v1/leads", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return CreateLeadResponseSchema.parse(json);
}

export async function updateLead(
  id: string,
  body: UpdateLeadRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadResponseSchema.parse(json);
}

export async function updateLeadStatus(
  id: string,
  body: UpdateLeadStatusRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadResponseSchema.parse(json);
}

export async function fetchLeadMessages(
  leadId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadMessageListResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(leadId)}/messages`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadMessageListResponseSchema.parse(json);
}

export async function addLeadMessage(
  leadId: string,
  body: {
    direction: "inbound" | "outbound";
    channel: "sms" | "email" | "internal_note";
    body: string;
    metadata?: Record<string, unknown>;
  },
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadMessageResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(leadId)}/messages`, {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadMessageResponseSchema.parse(json);
}

export async function fetchLeadsInbox(
  params: { limit?: number; cursor?: string } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<InboxLeadListResponse> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const json = await apiFetch<unknown>(`/api/v1/leads/inbox${query}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return InboxLeadListResponseSchema.parse(json);
}

/** PATCH /api/v1/contacts/:id — update contact fields (L4 contact edit form). */
export async function updateContact(
  contactId: string,
  body: UpdateContactRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContactResponse> {
  const json = await apiFetch<unknown>(`/api/v1/contacts/${encodeURIComponent(contactId)}`, {
    method: "PATCH",
    body: JSON.stringify(UpdateContactRequestSchema.parse(body)),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContactResponseSchema.parse(json);
}

export async function fetchLeadSequences(
  leadId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadSequenceListResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(leadId)}/sequences`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadSequenceListResponseSchema.parse(json);
}

export async function startLeadSequence(
  leadId: string,
  body: StartSequenceRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadSequenceResponse> {
  const json = await apiFetch<unknown>(`/api/v1/leads/${encodeURIComponent(leadId)}/sequences`, {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return LeadSequenceResponseSchema.parse(json);
}

export async function updateSequenceStatus(
  leadId: string,
  sequenceId: string,
  body: UpdateSequenceStatusRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<LeadSequenceResponse> {
  const json = await apiFetch<unknown>(
    `/api/v1/leads/${encodeURIComponent(leadId)}/sequences/${encodeURIComponent(sequenceId)}/status`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
      ...(options.getToken ? { getToken: options.getToken } : {}),
    },
  );
  return LeadSequenceResponseSchema.parse(json);
}

export async function fetchSequenceSteps(
  leadId: string,
  sequenceId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<SequenceStepListResponse> {
  const json = await apiFetch<unknown>(
    `/api/v1/leads/${encodeURIComponent(leadId)}/sequences/${encodeURIComponent(sequenceId)}/steps`,
    { ...(options.getToken ? { getToken: options.getToken } : {}) },
  );
  return SequenceStepListResponseSchema.parse(json);
}

// ── Content module API functions ─────────────────────────────────────────────

export async function fetchBrandVoices(
  options: { getToken?: GetTokenFn } = {},
): Promise<BrandVoiceListResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/brand-voices", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BrandVoiceListResponseSchema.parse(json);
}

export async function createBrandVoice(
  body: CreateBrandVoiceRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<BrandVoiceResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/brand-voices", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BrandVoiceResponseSchema.parse(json);
}

export async function updateBrandVoice(
  id: string,
  body: UpdateBrandVoiceRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<BrandVoiceResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/brand-voices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BrandVoiceResponseSchema.parse(json);
}

export async function deleteBrandVoice(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/content/brand-voices/${encodeURIComponent(id)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

export async function fetchPosts(
  params: {
    status?: ContentPostResponse["status"];
    platform?: ContentPostResponse["platform"];
    limit?: number;
    cursor?: string;
  } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.platform) qs.set("platform", params.platform);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const json = await apiFetch<unknown>(`/api/v1/content/posts${query}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostListResponseSchema.parse(json);
}

export async function fetchPost(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/posts/${encodeURIComponent(id)}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function createPost(
  body: CreateContentPostRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/posts", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function generatePost(
  body: GeneratePostRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/posts/generate", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

/** POST /api/v1/content/uploads — image upload to R2, returns the public URL. */
export async function uploadContentImage(
  file: File,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").ContentImageUploadResponse> {
  const { ContentImageUploadResponseSchema } = await import("@simpliwork/api-schema");
  const form = new FormData();
  form.append("file", file);
  const json = await apiFetch<unknown>("/api/v1/content/uploads", {
    method: "POST",
    body: form,
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentImageUploadResponseSchema.parse(json);
}

export async function updatePost(
  id: string,
  body: UpdateContentPostRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/posts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function deletePost(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/content/posts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

export async function schedulePost(
  id: string,
  body: SchedulePostRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/posts/${encodeURIComponent(id)}/schedule`, {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function publishPost(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/posts/${encodeURIComponent(id)}/publish`, {
    method: "POST",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function retryPost(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentPostResponse> {
  const json = await apiFetch<unknown>(`/api/v1/content/posts/${encodeURIComponent(id)}/retry`, {
    method: "POST",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentPostResponseSchema.parse(json);
}

export async function fetchContentAnalytics(
  options: { getToken?: GetTokenFn } = {},
): Promise<ContentAnalyticsResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/analytics", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return ContentAnalyticsResponseSchema.parse(json);
}

export async function fetchFunnel(
  options: { getToken?: GetTokenFn } = {},
): Promise<FunnelResponse> {
  const json = await apiFetch<unknown>("/api/v1/analytics/funnel", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return FunnelResponseSchema.parse(json);
}

/**
 * GET /api/v1/analytics/funnel — Combined funnel: content → clicks → leads → bookings.
 *
 * Alias for `fetchFunnel` exposed under the dashboard-facing name so the
 * `useFunnelAnalytics` hook can import from a stable symbol without coupling
 * the analytics hook module to the content-module api functions.
 */
export const fetchFunnelAnalytics = fetchFunnel;

export async function fetchConnections(
  options: { getToken?: GetTokenFn } = {},
): Promise<PlatformConnectionListResponse> {
  const json = await apiFetch<unknown>("/api/v1/content/connections", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return PlatformConnectionListResponseSchema.parse(json);
}

export async function upsertConnection(
  platform: string,
  body: UpsertPlatformConnectionRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<PlatformConnectionResponse> {
  const json = await apiFetch<unknown>(
    `/api/v1/content/connections/${encodeURIComponent(platform)}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
      ...(options.getToken ? { getToken: options.getToken } : {}),
    },
  );
  return PlatformConnectionResponseSchema.parse(json);
}

export async function deleteConnection(
  platform: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/content/connections/${encodeURIComponent(platform)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// ─── MCP connections + bindings (M2.7) ────────────────────────────────────────

export async function fetchMcpServers(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpServersListResponse> {
  const { McpServersListResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/mcp/servers", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return McpServersListResponseSchema.parse(json);
}

export async function fetchMcpInstances(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpInstancesListResponse> {
  const { McpInstancesListResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/connections", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return McpInstancesListResponseSchema.parse(json);
}

export async function createMcpInstance(
  body: import("@simpliwork/api-schema").CreateMcpInstanceRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpInstanceResponse> {
  const { McpInstanceResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/connections", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return McpInstanceResponseSchema.parse(json);
}

export async function deleteMcpInstance(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/connections/${encodeURIComponent(id)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

export async function fetchMcpBindings(
  agentId: string,
  agentVersion: number,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpBindingsListResponse> {
  const { McpBindingsListResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>(
    `/api/v1/mcp/bindings?agent_id=${encodeURIComponent(agentId)}&agent_version=${agentVersion}`,
    { ...(options.getToken ? { getToken: options.getToken } : {}) },
  );
  return McpBindingsListResponseSchema.parse(json);
}

export async function createMcpBinding(
  body: import("@simpliwork/api-schema").CreateMcpBindingRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpBindingResponse> {
  const { McpBindingResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/mcp/bindings", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return McpBindingResponseSchema.parse(json);
}

export async function deleteMcpBinding(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/mcp/bindings/${encodeURIComponent(id)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// ─── Billing (M3.3) ───────────────────────────────────────────────────────────

export async function fetchBudget(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").BudgetResponse> {
  const { BudgetResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/budgets", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BudgetResponseSchema.parse(json);
}

export async function fetchBudgetUsage(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").BudgetUsageResponse> {
  const { BudgetUsageResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/budgets/usage", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BudgetUsageResponseSchema.parse(json);
}

export async function updateBudget(
  body: import("@simpliwork/api-schema").UpdateBudgetRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").BudgetResponse> {
  const { BudgetResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/budgets", {
    method: "PUT",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BudgetResponseSchema.parse(json);
}

// ─── Stripe Billing (M1.1) ───────────────────────────────────────────────────

export async function fetchSubscription(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").SubscriptionResponse> {
  const { SubscriptionResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/subscription", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return SubscriptionResponseSchema.parse(json);
}

export async function createCheckoutSession(
  body: import("@simpliwork/api-schema").CreateCheckoutSessionRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").CheckoutSessionResponse> {
  const { CheckoutSessionResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/checkout", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return CheckoutSessionResponseSchema.parse(json);
}

export async function createBillingPortalSession(
  body: { return_url?: string } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").BillingPortalResponse> {
  const { BillingPortalResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/portal", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BillingPortalResponseSchema.parse(json);
}

// ─── Credits — wallet + top-ups + auto-refill (M1.5 / M1.6) ──────────────────

export async function fetchWallet(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").WalletResponse> {
  const { WalletResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/wallet", {
    method: "GET",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return WalletResponseSchema.parse(json);
}

export async function createTopUpCheckout(
  body: import("@simpliwork/api-schema").CreateTopUpCheckoutRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").CreateTopUpCheckoutResponse> {
  const { CreateTopUpCheckoutResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/topup", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return CreateTopUpCheckoutResponseSchema.parse(json);
}

export async function updateAutoRefill(
  body: import("@simpliwork/api-schema").UpdateAutoRefillRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").WalletResponse> {
  const { WalletResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/billing/auto-refill", {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return WalletResponseSchema.parse(json);
}

// ─── MCP Calls (M3.5) ────────────────────────────────────────────────────────

export async function fetchMcpCalls(
  params: { runId?: string; instanceId?: string; limit?: number } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").McpCallsListResponse> {
  const { McpCallsListResponseSchema } = await import("@simpliwork/api-schema");
  const qs = new URLSearchParams();
  if (params.runId) qs.set("run_id", params.runId);
  if (params.instanceId) qs.set("instance_id", params.instanceId);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const qstr = qs.toString();
  const json = await apiFetch<unknown>(`/api/v1/mcp/calls${qstr ? `?${qstr}` : ""}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return McpCallsListResponseSchema.parse(json);
}

// ─── Audit log (M3.8) ────────────────────────────────────────────────────────

export async function fetchAuditLog(
  params: { eventType?: string; limit?: number; before?: string } = {},
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").AuditListResponse> {
  const { AuditListResponseSchema } = await import("@simpliwork/api-schema");
  const qs = new URLSearchParams();
  if (params.eventType) qs.set("event_type", params.eventType);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.before) qs.set("before", params.before);
  const qstr = qs.toString();
  const json = await apiFetch<unknown>(`/api/v1/audit${qstr ? `?${qstr}` : ""}`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AuditListResponseSchema.parse(json);
}

// ─── Team management (M3.9) ──────────────────────────────────────────────────

export async function fetchTeamMembers(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").TeamMembersResponse> {
  const { TeamMembersResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/team/members", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TeamMembersResponseSchema.parse(json);
}

export async function fetchTeamInvites(
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").TeamInvitesResponse> {
  const { TeamInvitesResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/team/invites", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TeamInvitesResponseSchema.parse(json);
}

export async function createTeamInvite(
  body: import("@simpliwork/api-schema").CreateInviteRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<import("@simpliwork/api-schema").CreateInviteResponse> {
  const { CreateInviteResponseSchema } = await import("@simpliwork/api-schema");
  const json = await apiFetch<unknown>("/api/v1/team/invites", {
    method: "POST",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return CreateInviteResponseSchema.parse(json);
}

export async function revokeTeamInvite(
  id: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<void>(`/api/v1/team/invites/${encodeURIComponent(id)}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// ── Settings ─────────────────────────────────────────────────────────────────

export type { TwilioSettingsResponse, UpdateTwilioSettingsRequest };

export async function fetchTwilioSettings(
  options: { getToken?: GetTokenFn } = {},
): Promise<TwilioSettingsResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/twilio", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TwilioSettingsResponseSchema.parse(json);
}

export async function updateTwilioSettings(
  body: UpdateTwilioSettingsRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<TwilioSettingsResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/twilio", {
    method: "PUT",
    body: JSON.stringify(UpdateTwilioSettingsRequestSchema.parse(body)),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TwilioSettingsResponseSchema.parse(json);
}

// ── Booking link settings ──────────────────────────────────────────────────────

export type { BookingLinkResponse, UpdateBookingLinkRequest };

export async function fetchBookingLink(
  options: { getToken?: GetTokenFn } = {},
): Promise<BookingLinkResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/booking", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BookingLinkResponseSchema.parse(json);
}

export async function updateBookingLink(
  body: UpdateBookingLinkRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<BookingLinkResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/booking", {
    method: "PUT",
    body: JSON.stringify(UpdateBookingLinkRequestSchema.parse(body)),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return BookingLinkResponseSchema.parse(json);
}

// ── Integrations (social OAuth) ────────────────────────────────────────────────

export type { IntegrationConnectResponse, IntegrationsListResponse, SocialPlatform };

export async function fetchIntegrations(
  options: { getToken?: GetTokenFn } = {},
): Promise<IntegrationsListResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/integrations", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return IntegrationsListResponseSchema.parse(json);
}

export async function connectIntegration(
  platform: SocialPlatform,
  options: { getToken?: GetTokenFn } = {},
): Promise<IntegrationConnectResponse> {
  const json = await apiFetch<unknown>(`/api/v1/settings/integrations/${platform}/connect`, {
    method: "POST",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return IntegrationConnectResponseSchema.parse(json);
}

export async function disconnectIntegration(
  platform: SocialPlatform,
  options: { getToken?: GetTokenFn } = {},
): Promise<void> {
  await apiFetch<unknown>(`/api/v1/settings/integrations/${platform}`, {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// ── Email lead ingest (M-EML) ─────────────────────────────────────────────────

export type {
  EmailConnectionConnectResponse,
  EmailConnectionResponse,
  UpdateEmailConnectionRequest,
};

export async function fetchEmailConnection(
  options: { getToken?: GetTokenFn } = {},
): Promise<EmailConnectionResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/email-connection", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return EmailConnectionResponseSchema.parse(json);
}

export async function connectEmailInbox(
  options: { getToken?: GetTokenFn } = {},
): Promise<EmailConnectionConnectResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/email-connection/connect", {
    method: "POST",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return EmailConnectionConnectResponseSchema.parse(json);
}

export async function updateEmailConnection(
  body: UpdateEmailConnectionRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<EmailConnectionResponse> {
  const json = await apiFetch<unknown>("/api/v1/settings/email-connection", {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return EmailConnectionResponseSchema.parse(json);
}

export async function disconnectEmailInbox(options: { getToken?: GetTokenFn } = {}): Promise<void> {
  await apiFetch<void>("/api/v1/settings/email-connection", {
    method: "DELETE",
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
}

// Billing, audit, team types (Phase 3) — re-exported from api-schema for
// call-site convenience so consumers import from one place.
export type {
  AuditEvent,
  AuditListResponse,
  BillingPortalResponse,
  BudgetResponse,
  BudgetUsageResponse,
  ContentImageUploadResponse,
  CostEvent,
  CreateCheckoutSessionRequest,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateTopUpCheckoutRequest,
  CreateTopUpCheckoutResponse,
  McpCallResponse,
  McpCallsListResponse,
  RunCostEventsResponse,
  SubscriptionResponse,
  TeamInvite,
  TeamInvitesResponse,
  TeamMember,
  TeamMembersResponse,
  TopUpPackId,
  UpdateAutoRefillRequest,
  UpdateBudgetRequest,
  WalletResponse,
} from "@simpliwork/api-schema";
// ── Agent Tool Packages (P2.5-B) ──────────────────────────────────────────────

export type { AgentToolPackagesResponse, UpdateAgentToolPackagesRequest };

/** GET /api/v1/agents/:id/tool-packages — enabled packages + available metadata. */
export async function fetchAgentToolPackages(
  agentId: string,
  options: { getToken?: GetTokenFn } = {},
): Promise<AgentToolPackagesResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}/tool-packages`, {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AgentToolPackagesResponseSchema.parse(json);
}

/** PUT /api/v1/agents/:id/tool-packages — update the enabled set. */
export async function updateAgentToolPackages(
  agentId: string,
  body: UpdateAgentToolPackagesRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<AgentToolPackagesResponse> {
  const json = await apiFetch<unknown>(`/api/v1/agents/${agentId}/tool-packages`, {
    method: "PUT",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return AgentToolPackagesResponseSchema.parse(json);
}

// ─── P2.5-C — Triggers API helpers ───────────────────────────────────────────

/** GET /api/v1/triggers — list all trigger assignments for the active tenant. */
export async function fetchTriggers(options: { getToken?: GetTokenFn } = {}): Promise<TriggerListResponse> {
  const json = await apiFetch<unknown>("/api/v1/triggers", {
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TriggerListResponseSchema.parse(json);
}

/** PUT /api/v1/triggers/:type — upsert a trigger assignment. */
export async function upsertTrigger(
  triggerType: string,
  body: UpsertTriggerRequest,
  options: { getToken?: GetTokenFn } = {},
): Promise<TriggerAssignment> {
  const json = await apiFetch<unknown>(`/api/v1/triggers/${triggerType}`, {
    method: "PUT",
    body: JSON.stringify(body),
    ...(options.getToken ? { getToken: options.getToken } : {}),
  });
  return TriggerAssignmentSchema.parse(json);
}

// Re-export trigger types from this module.
export type { TriggerAssignment, TriggerListResponse, UpsertTriggerRequest };

// Re-export the response types so call-sites can import them from one place.
export type {
  AgentDetailResponse,
  AgentResponse,
  AgentVersionResponse,
  // Contact update (L4)
  ContactResponse,
  UpdateContactRequest,
  // Leads module
  BrandVoiceListResponse,
  BrandVoiceResponse,
  ContentAnalyticsItem,
  ContentAnalyticsResponse,
  ContentPostListResponse,
  ContentPostResponse,
  ContentPostResponse as PostResponse,
  CreateAgentRequest,
  CreateAgentResponse,
  CreateBrandVoiceRequest,
  CreateContentPostRequest,
  CreateLeadRequest,
  CreateLeadResponse,
  CreateSecretRequest,
  FunnelResponse,
  GeneratePostRequest,
  InboxLeadListResponse,
  InboxLeadResponse,
  LeadAnalyticsResponse,
  LeadDetailResponse,
  LeadListResponse,
  LeadMessageListResponse,
  LeadMessageResponse,
  LeadResponse,
  LeadSequenceListResponse,
  LeadSequenceResponse,
  ListRunsResponse,
  PlatformConnectionListResponse,
  PlatformConnectionResponse,
  RunResponse,
  SchedulePostRequest,
  SecretCreateResponse,
  SecretListResponse,
  SequenceStepListResponse,
  SequenceStepResponse,
  StartSequenceRequest,
  TriggerRunRequest,
  TriggerRunResponse,
  UpdateAgentRequest,
  UpdateAgentResponse,
  UpdateBrandVoiceRequest,
  UpdateContentPostRequest,
  UpdateLeadRequest,
  UpdateLeadStatusRequest,
  UpdateSequenceStatusRequest,
  UpsertPlatformConnectionRequest,
};
